import { mermaid } from '@streamdown/mermaid'
import type { FileUIPart, UIMessage } from 'ai'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PaperclipIcon,
  XIcon,
} from 'lucide-react'
import type { ComponentProps, HTMLAttributes, ReactElement } from 'react'
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Streamdown, useIsCodeFenceIncomplete } from 'streamdown'
import type {
  CustomRenderer,
  MermaidErrorComponentProps,
  MermaidOptions,
} from 'streamdown'
import { CodeBlock } from './code-block'
import { VegaLiteRenderer } from './vega-lite-renderer'
import { TourRenderer } from './tour-renderer'
import { DiffRenderer } from './diff-renderer'
import { Button } from '@/components/ui/button'
import { ButtonGroup, ButtonGroupText } from '@/components/ui/button-group'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useMountEffect } from '@/hooks/useMountEffect'
import { cn } from '@/lib/utils'

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage['role']
}

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      'group flex w-full max-w-[95%] flex-col gap-2',
      from === 'user' ? 'is-user ml-auto justify-end' : 'is-assistant',
      className
    )}
    {...props}
  />
)

export type MessageContentProps = HTMLAttributes<HTMLDivElement>

export const MessageContent = ({
  children,
  className,
  ...props
}: MessageContentProps) => (
  <div
    className={cn(
      'is-user:dark flex w-fit max-w-full min-w-0 flex-col gap-2 overflow-hidden text-sm',
      'group-[.is-user]:ml-auto group-[.is-user]:rounded-lg group-[.is-user]:bg-secondary group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground',
      'group-[.is-assistant]:text-foreground',
      className
    )}
    {...props}>
    {children}
  </div>
)

export type MessageActionsProps = ComponentProps<'div'>

export const MessageActions = ({
  className,
  children,
  ...props
}: MessageActionsProps) => (
  <div className={cn('flex items-center gap-1', className)} {...props}>
    {children}
  </div>
)

export type MessageActionProps = ComponentProps<typeof Button> & {
  tooltip?: string
  label?: string
}

export const MessageAction = ({
  tooltip,
  children,
  label,
  variant = 'ghost',
  size = 'icon-sm',
  ...props
}: MessageActionProps) => {
  const button = (
    <Button size={size} type="button" variant={variant} {...props}>
      {children}
      <span className="sr-only">{label || tooltip}</span>
    </Button>
  )

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return button
}

interface MessageBranchContextType {
  currentBranch: number
  totalBranches: number
  goToPrevious: () => void
  goToNext: () => void
  branches: ReactElement[]
  setBranches: (branches: ReactElement[]) => void
}

const MessageBranchContext = createContext<MessageBranchContextType | null>(
  null
)

const useMessageBranch = () => {
  const context = useContext(MessageBranchContext)

  if (!context) {
    throw new Error(
      'MessageBranch components must be used within MessageBranch'
    )
  }

  return context
}

export type MessageBranchProps = HTMLAttributes<HTMLDivElement> & {
  defaultBranch?: number
  onBranchChange?: (branchIndex: number) => void
}

export const MessageBranch = ({
  defaultBranch = 0,
  onBranchChange,
  className,
  ...props
}: MessageBranchProps) => {
  const [currentBranch, setCurrentBranch] = useState(defaultBranch)
  const [branches, setBranches] = useState<ReactElement[]>([])

  const handleBranchChange = useCallback(
    (newBranch: number) => {
      setCurrentBranch(newBranch)
      onBranchChange?.(newBranch)
    },
    [onBranchChange]
  )

  const goToPrevious = useCallback(() => {
    const newBranch =
      currentBranch > 0 ? currentBranch - 1 : branches.length - 1
    handleBranchChange(newBranch)
  }, [currentBranch, branches.length, handleBranchChange])

  const goToNext = useCallback(() => {
    const newBranch =
      currentBranch < branches.length - 1 ? currentBranch + 1 : 0
    handleBranchChange(newBranch)
  }, [currentBranch, branches.length, handleBranchChange])

  const contextValue = useMemo<MessageBranchContextType>(
    () => ({
      currentBranch,
      totalBranches: branches.length,
      goToPrevious,
      goToNext,
      branches,
      setBranches,
    }),
    [currentBranch, branches, goToPrevious, goToNext, setBranches]
  )

  return (
    <MessageBranchContext.Provider value={contextValue}>
      <div
        className={cn('grid w-full gap-2 [&>div]:pb-0', className)}
        {...props}
      />
    </MessageBranchContext.Provider>
  )
}

export type MessageBranchContentProps = HTMLAttributes<HTMLDivElement>

export const MessageBranchContent = ({
  children,
  ...props
}: MessageBranchContentProps) => {
  const { currentBranch, setBranches, branches } = useMessageBranch()
  const childrenArray = useMemo(
    () => (Array.isArray(children) ? children : [children]),
    [children]
  )

  // Use useEffect to update branches when they change
  useEffect(() => {
    if (branches.length !== childrenArray.length) {
      setBranches(childrenArray)
    }
  }, [childrenArray, branches, setBranches])

  return childrenArray.map((branch, index) => (
    <div
      className={cn(
        'grid gap-2 overflow-hidden [&>div]:pb-0',
        index === currentBranch ? 'block' : 'hidden'
      )}
      key={branch.key}
      {...props}>
      {branch}
    </div>
  ))
}

export type MessageBranchSelectorProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage['role']
}

export const MessageBranchSelector = ({
  className: _className,
  from: _from,
  ...props
}: MessageBranchSelectorProps) => {
  const { totalBranches } = useMessageBranch()

  // Don't render if there's only one branch
  if (totalBranches <= 1) {
    return null
  }

  return (
    <ButtonGroup
      className="[&>*:not(:first-child)]:rounded-l-md [&>*:not(:last-child)]:rounded-r-md"
      orientation="horizontal"
      {...props}
    />
  )
}

export type MessageBranchPreviousProps = ComponentProps<typeof Button>

export const MessageBranchPrevious = ({
  children,
  ...props
}: MessageBranchPreviousProps) => {
  const { goToPrevious, totalBranches } = useMessageBranch()

  return (
    <Button
      aria-label="Previous branch"
      disabled={totalBranches <= 1}
      onClick={goToPrevious}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}>
      {children ?? <ChevronLeftIcon size={14} />}
    </Button>
  )
}

export type MessageBranchNextProps = ComponentProps<typeof Button>

export const MessageBranchNext = ({
  children,
  className: _className,
  ...props
}: MessageBranchNextProps) => {
  const { goToNext, totalBranches } = useMessageBranch()

  return (
    <Button
      aria-label="Next branch"
      disabled={totalBranches <= 1}
      onClick={goToNext}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}>
      {children ?? <ChevronRightIcon size={14} />}
    </Button>
  )
}

export type MessageBranchPageProps = HTMLAttributes<HTMLSpanElement>

export const MessageBranchPage = ({
  className,
  ...props
}: MessageBranchPageProps) => {
  const { currentBranch, totalBranches } = useMessageBranch()

  return (
    <ButtonGroupText
      className={cn(
        'border-none bg-transparent text-muted-foreground shadow-none',
        className
      )}
      {...props}>
      {currentBranch + 1} of {totalBranches}
    </ButtonGroupText>
  )
}

export type MessageResponseProps = ComponentProps<typeof Streamdown>

// Module-level (stable identity) so it survives the memo comparator below,
// which only compares `children`. Applies to every chat message; agents
// without these conventions (e.g. Hanna) simply never emit the fences.
// Charts are `vega-lite` only (what David's prompt teaches) — not the broader,
// riskier full `vega` grammar.
const CUSTOM_RENDERERS: CustomRenderer[] = [
  { language: ['vega-lite'], component: VegaLiteRenderer },
  { language: ['tour'], component: TourRenderer },
  { language: ['diff'], component: DiffRenderer },
]

// Streamdown doesn't forward isIncomplete on the mermaid branch, so a
// half-written fence lands here as an error; the fence context tells "arriving"
// apart from "broken".
const MermaidFallback = ({ chart }: MermaidErrorComponentProps) => {
  const isStreaming = useIsCodeFenceIncomplete()
  if (isStreaming) return <Skeleton className="h-60 w-full" />
  return <CodeBlock code={chart} language="mermaid" label="diagram source" />
}

// Streamdown's context memo watches the `mermaid` prop — a fresh object re-runs
// getMermaid and re-renders every diagram.
const MERMAID_OPTIONS: MermaidOptions = {
  config: {
    // Model-authored SVG is injected with dangerouslySetInnerHTML, bypassing
    // rehype-sanitize; mermaid's DOMPurify pass is the only guard.
    securityLevel: 'strict',
    // foreignObject `<br/>` re-serializes to `<br>` — invalid XML, so PNG
    // export silently no-ops.
    htmlLabels: false,
  },
  errorComponent: MermaidFallback,
}

// Streamdown zooms on any wheel — no ctrlKey check, hardcoded 0.1 step, no
// config knob. Trackpads differ; retune if it feels wrong on real hardware.
const PINCH_TICKS_PER_ZOOM_STEP = 5

const useDampenedMermaidZoom = () => {
  const ref = useRef<HTMLDivElement>(null)
  useMountEffect(() => {
    const el = ref.current
    if (!el) return
    let ticks = 0
    const onWheel = (event: WheelEvent) => {
      const target = event.target
      if (
        !(target instanceof Element) ||
        !target.closest('[data-streamdown="mermaid"]')
      ) {
        return
      }
      if (!event.ctrlKey) {
        event.stopPropagation()
        return
      }
      ticks += 1
      // On the tick we let through, Streamdown's own handler blocks page zoom.
      if (ticks % PINCH_TICKS_PER_ZOOM_STEP !== 0) {
        event.preventDefault()
        event.stopPropagation()
      }
    }
    el.addEventListener('wheel', onWheel, { capture: true, passive: false })
    return () => el.removeEventListener('wheel', onWheel, { capture: true })
  })
  return ref
}

export const MessageResponse = memo(
  ({ className, plugins, ...props }: MessageResponseProps) => {
    // Stable ref so streaming re-renders don't hand Streamdown a new plugins
    // object every token. `plugins` is usually undefined (no caller passes it).
    const mergedPlugins = useMemo(
      () => ({ renderers: CUSTOM_RENDERERS, mermaid, ...plugins }),
      [plugins]
    )
    const wheelGuardRef = useDampenedMermaidZoom()
    return (
      // `contents` generates no box, so Streamdown's `size-full` still resolves
      // against MessageContent.
      <div ref={wheelGuardRef} className="contents">
        <Streamdown
          className={cn(
            'size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
            className
          )}
          plugins={mergedPlugins}
          mermaid={MERMAID_OPTIONS}
          {...props}
        />
      </div>
    )
  },
  // Streaming props matter as well as children: a run can end with the text
  // unchanged (an unclosed fence), and skipping that render would strand
  // MermaidFallback on a skeleton forever.
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.isAnimating === nextProps.isAnimating &&
    prevProps.mode === nextProps.mode
)

MessageResponse.displayName = 'MessageResponse'

export type MessageAttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: FileUIPart
  className?: string
  onRemove?: () => void
}

export function MessageAttachment({
  data,
  className,
  onRemove,
  ...props
}: MessageAttachmentProps) {
  const filename = data.filename || ''
  const mediaType =
    data.mediaType?.startsWith('image/') && data.url ? 'image' : 'file'
  const isImage = mediaType === 'image'
  const attachmentLabel = filename || (isImage ? 'Image' : 'Attachment')

  return (
    <div
      className={cn(
        'group relative size-24 overflow-hidden rounded-lg',
        className
      )}
      {...props}>
      {isImage ? (
        <>
          <img
            alt={filename || 'attachment'}
            className="size-full object-cover"
            height={100}
            src={data.url}
            width={100}
          />
          {onRemove && (
            <Button
              aria-label="Remove attachment"
              className="absolute top-2 right-2 size-6 rounded-full bg-background/80 p-0 backdrop-blur-sm transition-opacity hover:bg-background md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 [&>svg]:size-3"
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
              type="button"
              variant="ghost">
              <XIcon />
              <span className="sr-only">Remove</span>
            </Button>
          )}
        </>
      ) : (
        <>
          {/* Own provider, like MessageAction above: this app has no root one. */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex size-full shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <PaperclipIcon className="size-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{attachmentLabel}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {onRemove && (
            <Button
              aria-label="Remove attachment"
              className="absolute top-2 right-2 size-6 rounded-full bg-background/80 p-0 backdrop-blur-sm transition-opacity hover:bg-background md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 [&>svg]:size-3"
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
              type="button"
              variant="ghost">
              <XIcon />
              <span className="sr-only">Remove</span>
            </Button>
          )}
        </>
      )}
    </div>
  )
}

export type MessageAttachmentsProps = ComponentProps<'div'>

export function MessageAttachments({
  children,
  className,
  ...props
}: MessageAttachmentsProps) {
  if (!children) {
    return null
  }

  return (
    <div
      className={cn(
        'ml-auto flex w-fit flex-wrap items-start gap-2',
        className
      )}
      {...props}>
      {children}
    </div>
  )
}

export type MessageToolbarProps = ComponentProps<'div'>

export const MessageToolbar = ({
  className,
  children,
  ...props
}: MessageToolbarProps) => (
  <div
    className={cn(
      'mt-4 flex w-full items-center justify-between gap-4',
      className
    )}
    {...props}>
    {children}
  </div>
)

/** Demo component for preview */
export default function MessageDemo() {
  return (
    <TooltipProvider>
      <div className="flex w-full max-w-2xl flex-col gap-4 p-6">
        <Message from="user">
          <MessageContent>
            <p>Can you explain how React hooks work?</p>
          </MessageContent>
        </Message>
        <Message from="assistant">
          <MessageContent>
            <MessageResponse>
              React hooks are functions that let you **hook into** React state
              and lifecycle features from function components. The most common
              hooks are: - `useState` - for managing local state - `useEffect` -
              for side effects like data fetching - `useContext` - for consuming
              context values Would you like me to show you some examples?
            </MessageResponse>
          </MessageContent>
        </Message>
        <Message from="user">
          <MessageContent>
            <p>Yes please, show me a useState example!</p>
          </MessageContent>
        </Message>
      </div>
    </TooltipProvider>
  )
}
